import { useEffect, useState, type FormEvent } from "react";
import { Link, Navigate, useLocation, useNavigate } from "react-router-dom";
import { ApiError, authApi } from "@/lib/api";
import { useAuth } from "@/lib/useAuth";
import { getOaCallbackUrl, newOaState, rememberOaRedirect } from "@/lib/oaAuth";
import { Button } from "@/components/ui/Button";
import { Field, Input } from "@/components/ui/Field";
import { Alert } from "@/components/ui/Alert";

interface LocationState {
  from?: { pathname: string };
}

export function LoginPage(): JSX.Element {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [oaSubmitting, setOaSubmitting] = useState(false);
  const [passwordLoginEnabled, setPasswordLoginEnabled] = useState(true);
  const [ssoConfigured, setSsoConfigured] = useState(true);

  useEffect(() => {
    let cancelled = false;
    authApi
      .oaStatus()
      .then((s) => {
        if (cancelled) return;
        setPasswordLoginEnabled(s.password_login_enabled);
        setSsoConfigured(s.sso_configured);
      })
      .catch(() => {
        // Older backends without OA routes: keep password form only.
        if (!cancelled) {
          setPasswordLoginEnabled(true);
          setSsoConfigured(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state.status === "authenticated") {
    const from = (location.state as LocationState | null)?.from?.pathname ?? "/";
    return <Navigate to={from} replace />;
  }

  const fromPath = (location.state as LocationState | null)?.from?.pathname ?? "/";

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await login(username.trim(), password);
      navigate(fromPath, { replace: true });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("登录失败, 请稍后重试");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onOaLogin = async (): Promise<void> => {
    setError(null);
    setOaSubmitting(true);
    try {
      const stateToken = newOaState();
      rememberOaRedirect(fromPath, stateToken);
      const service = getOaCallbackUrl(stateToken);
      const { login_url } = await authApi.oaLoginUrl(service);
      window.location.href = login_url;
    } catch (err) {
      setOaSubmitting(false);
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError("无法发起 OA 登录，请稍后重试");
      }
    }
  };

  return (
    <div className="flex min-h-full items-center justify-center bg-zinc-50 px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-zinc-900">Liando</h1>
          <p className="mt-1 text-sm text-zinc-500">登录以提交报告任务</p>
        </div>
        <div className="space-y-5 rounded-xl border border-zinc-200 bg-white p-6 shadow-sm">
          {error ? <Alert tone="error">{error}</Alert> : null}

          {ssoConfigured ? (
            <div className="space-y-2">
              <Button
                type="button"
                className="w-full"
                disabled={oaSubmitting || submitting}
                onClick={() => void onOaLogin()}
              >
                {oaSubmitting ? "跳转 OA 中…" : "使用公司 OA 登录"}
              </Button>
              {passwordLoginEnabled ? (
                <p className="text-center text-xs text-zinc-400">或使用账号密码</p>
              ) : null}
            </div>
          ) : null}

          {passwordLoginEnabled ? (
            <form onSubmit={onSubmit} className="space-y-5">
              <Field id="username" label="用户名" required>
                <Input
                  id="username"
                  name="username"
                  autoComplete="username"
                  required
                  minLength={3}
                  maxLength={32}
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={submitting || oaSubmitting}
                />
              </Field>
              <Field id="password" label="密码" required>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  minLength={8}
                  maxLength={128}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={submitting || oaSubmitting}
                />
              </Field>
              <Button type="submit" className="w-full" disabled={submitting || oaSubmitting}>
                {submitting ? "登录中…" : "登录"}
              </Button>
              <p className="text-center text-xs text-zinc-500">
                首次部署?{" "}
                <Link to="/init-admin" className="font-medium text-zinc-900 hover:underline">
                  初始化管理员
                </Link>
              </p>
            </form>
          ) : (
            <p className="text-center text-xs text-zinc-500">请使用公司 OA 账号登录</p>
          )}
        </div>
      </div>
    </div>
  );
}
